import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Trash2, User as UserIcon } from "lucide-react";

export default function UsersTab({ users, currentUser, API, authHeaders, onReload }) {
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const changeUserRole = async (targetUser, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${targetUser.user_id}/role`, { role: newRole }, { headers: authHeaders() });
      toast.success(`${targetUser.email} → ${newRole.toUpperCase()}`);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Role update failed");
    }
  };

  const cycleUserRole = (targetUser) => {
    const currentIsOwnerActor = currentUser.role === "owner";
    const order = currentIsOwnerActor ? ["customer", "admin", "owner"] : ["customer", "admin"];
    const i = order.indexOf(targetUser.role);
    if (targetUser.role === "owner" && !currentIsOwnerActor) return;
    changeUserRole(targetUser, order[(i + 1) % order.length]);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setDeletingUser(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteUserTarget.user_id}`, { headers: authHeaders() });
      toast.success(`Deleted ${deleteUserTarget.email}`);
      setDeleteUserTarget(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
    setDeletingUser(false);
  };

  return (
    <div data-testid="users-tab">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div>
          <div className="font-display text-2xl">SIGNED-UP USERS</div>
          <div className="font-mono text-xs text-[var(--ink-2)]">{users.length} total · {users.filter(u=>u.role==='customer').length} customers · {users.filter(u=>u.role==='admin').length} admins · {users.filter(u=>u.role==='owner').length} owners</div>
        </div>
      </div>

      {/* Desktop: table view (md+) */}
      <div className="hidden md:block card-industrial overflow-hidden">
        <table className="w-full font-mono text-sm">
          <thead className="bg-[var(--ink)] text-[var(--paper)]">
            <tr>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Name</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Email</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Role</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Joined</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan="5" className="text-center py-10">No users yet.</td></tr>}
            {users.map(u => (
              <UserTableRow
                key={u.user_id}
                u={u}
                currentUser={currentUser}
                onCycle={cycleUserRole}
                onDelete={setDeleteUserTarget}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card view (< md) */}
      <div className="md:hidden space-y-3">
        {users.length === 0 && <div className="text-center py-10 font-mono">No users yet.</div>}
        {users.map(u => (
          <UserMobileCard
            key={u.user_id}
            u={u}
            currentUser={currentUser}
            onCycle={cycleUserRole}
            onDelete={setDeleteUserTarget}
          />
        ))}
      </div>

      <p className="font-mono text-xs mt-4 text-[var(--ink-2)]">Tip: Tap the role button to cycle <strong>CUSTOMER ▸ ADMIN ▸ OWNER ▸ CUSTOMER</strong>. Only owners can assign the owner role. You cannot change your own role.</p>

      {deleteUserTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={()=>!deletingUser && setDeleteUserTarget(null)} data-testid="delete-user-modal">
          <div className="bg-[var(--paper)] border-2 border-[var(--ink)] max-w-md w-full" onClick={e=>e.stopPropagation()}>
            <div className="p-5 border-b-2 border-[var(--ink)] flex items-center gap-3">
              <div className="bg-[var(--accent)] text-white w-10 h-10 flex items-center justify-center border-2 border-[var(--ink)]"><Trash2 size={18}/></div>
              <h2 className="font-display text-xl">DELETE USER?</h2>
            </div>
            <div className="p-5">
              <p className="font-mono text-sm mb-2">This will permanently remove this account and end all its sessions:</p>
              <div className="bg-[var(--muted)] border-2 border-[var(--ink)] p-3 mb-4">
                <div className="font-display text-base">{deleteUserTarget.name || '—'}</div>
                <div className="font-mono text-xs break-all">{deleteUserTarget.email}</div>
                <div className="font-mono text-[10px] uppercase mt-1 text-[var(--ink-2)]">Role: {deleteUserTarget.role}</div>
              </div>
              <p className="font-mono text-xs text-[var(--ink-2)]">This action cannot be undone. Their order history will remain.</p>
            </div>
            <div className="p-5 border-t-2 border-[var(--ink)] flex gap-3 justify-end">
              <button onClick={()=>setDeleteUserTarget(null)} disabled={deletingUser} className="btn-outline" data-testid="delete-user-no-btn">NO, KEEP USER</button>
              <button onClick={confirmDeleteUser} disabled={deletingUser} className="btn-primary bg-[var(--accent)] border-[var(--accent)]" data-testid="delete-user-yes-btn">{deletingUser?"DELETING...":"YES, DELETE"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function roleClass(role) {
  return role === "owner" ? "bg-[var(--accent)] text-white border-[var(--accent)]"
       : role === "admin" ? "bg-blue-400 border-[var(--ink)]"
       : "bg-[var(--paper)] border-[var(--ink)]";
}

function UserTableRow({ u, currentUser, onCycle, onDelete }) {
  const isSelf = u.user_id === currentUser.user_id;
  const isOwner = u.role === 'owner';
  const isAdmin = u.role === 'admin';
  const canOwnerActions = currentUser.role === 'owner';
  const order = canOwnerActions ? ["customer","admin","owner"] : ["customer","admin"];
  const canCycle = !isSelf && !(isOwner && !canOwnerActions);
  const canDelete = !isSelf && (!isOwner || canOwnerActions);
  const nextRole = canCycle ? order[(order.indexOf(u.role)+1) % order.length] : null;

  return (
    <tr className="border-b border-[var(--muted)] hover:bg-[var(--muted)]/40" data-testid={`user-row-${u.user_id}`}>
      <td className="py-3 px-4 flex items-center gap-2">
        {isOwner ? <Shield size={14} className="text-[var(--accent)]"/> : isAdmin ? <Shield size={14} className="text-blue-600"/> : <UserIcon size={14} className="text-[var(--ink-2)]"/>}
        <span className="font-display">{u.name || '—'}</span>
        {isSelf && <span className="bg-[var(--accent-2)] border border-[var(--ink)] px-1.5 py-0.5 text-[10px] uppercase">you</span>}
      </td>
      <td className="py-3 px-4 text-xs break-all">{u.email}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 border-2 border-[var(--ink)] text-xs uppercase font-bold ${isOwner?'bg-[var(--accent)] text-white':isAdmin?'bg-blue-300':'bg-[var(--muted)]'}`}>{u.role}</span>
      </td>
      <td className="py-3 px-4 text-xs text-[var(--ink-2)]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
      <td className="py-3 px-4 text-right">
        <div className="inline-flex gap-2">
          {isSelf ? (
            <span className="text-[10px] text-[var(--ink-2)]">—</span>
          ) : (
            <>
              {canCycle && (
                <button onClick={()=>onCycle(u)} className={`inline-flex items-center gap-2 px-3 py-2 border-2 font-mono text-xs font-bold uppercase tracking-wider hover:shadow-[3px_3px_0_var(--ink)] active:translate-x-[1px] active:translate-y-[1px] transition ${roleClass(u.role)}`} data-testid={`cycle-${u.user_id}`}>
                  <Shield size={12}/>
                  <span>{u.role}</span>
                  <span className="opacity-60">▸</span>
                  <span>{nextRole}</span>
                </button>
              )}
              {canDelete && (
                <button onClick={()=>onDelete(u)} className="inline-flex items-center gap-1 px-3 py-2 border-2 border-[var(--ink)] bg-[var(--paper)] font-mono text-xs font-bold uppercase tracking-wider hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)] active:translate-x-[1px] active:translate-y-[1px] transition" data-testid={`delete-user-${u.user_id}`} aria-label="Delete user">
                  <Trash2 size={12}/>
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function UserMobileCard({ u, currentUser, onCycle, onDelete }) {
  const isSelf = u.user_id === currentUser.user_id;
  const isOwner = u.role === 'owner';
  const isAdmin = u.role === 'admin';
  const canOwnerActions = currentUser.role === 'owner';
  const order = canOwnerActions ? ["customer","admin","owner"] : ["customer","admin"];
  const canCycle = !isSelf && !(isOwner && !canOwnerActions);
  const canDelete = !isSelf && (!isOwner || canOwnerActions);
  const nextRole = canCycle ? order[(order.indexOf(u.role)+1) % order.length] : null;

  return (
    <div className="card-industrial p-4" data-testid={`user-card-${u.user_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {isOwner ? <Shield size={16} className="text-[var(--accent)]"/> : isAdmin ? <Shield size={16} className="text-blue-600"/> : <UserIcon size={16} className="text-[var(--ink-2)]"/>}
          <div>
            <div className="font-display text-base flex items-center gap-2">{u.name || '—'}{isSelf && <span className="bg-[var(--accent-2)] border border-[var(--ink)] px-1.5 py-0.5 text-[10px] uppercase">you</span>}</div>
            <div className="font-mono text-xs text-[var(--ink-2)] break-all">{u.email}</div>
          </div>
        </div>
        <span className={`px-2 py-1 border-2 border-[var(--ink)] text-[10px] uppercase font-bold flex-shrink-0 ${isOwner?'bg-[var(--accent)] text-white':isAdmin?'bg-blue-300':'bg-[var(--muted)]'}`}>{u.role}</span>
      </div>
      <div className="font-mono text-[10px] text-[var(--ink-2)] mb-3">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
      {isSelf ? (
        <div className="font-mono text-xs text-[var(--ink-2)]">Self-edit blocked</div>
      ) : (
        <div className="flex flex-col gap-2">
          {canCycle && (
            <button onClick={()=>onCycle(u)} className={`w-full inline-flex items-center justify-center gap-2 px-3 py-3 border-2 font-mono text-xs font-bold uppercase tracking-wider active:translate-x-[1px] active:translate-y-[1px] transition ${roleClass(u.role)}`} data-testid={`m-cycle-${u.user_id}`}>
              <Shield size={14}/>
              <span>{u.role}</span>
              <span className="opacity-60">▸</span>
              <span>{nextRole}</span>
            </button>
          )}
          {canDelete && (
            <button onClick={()=>onDelete(u)} className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 border-2 border-[var(--ink)] bg-[var(--paper)] font-mono text-xs font-bold uppercase tracking-wider hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)] active:translate-x-[1px] active:translate-y-[1px] transition" data-testid={`m-delete-${u.user_id}`}>
              <Trash2 size={14}/> DELETE USER
            </button>
          )}
        </div>
      )}
    </div>
  );
}
